import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CancelToken, isCancel } from 'axios';
import api from '../services/api';
import { navigateBack, getCourseLessonsPath } from '../utils/navigationHelper';
import { CloudArrowUpIcon, TrashIcon, PlusIcon, EyeIcon, PencilIcon } from '@heroicons/react/24/outline';
import LessonTextRenderer from '../components/LessonTextRenderer';
import RichTextEditor from '../components/RichTextEditor';

const LessonFormPage = () => {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = !!lessonId;

  const getDefaultFormData = () => ({
    title: '',
    description: '',
    contentType: 'text',
    textContent: '',
    contentUrl: '',
    studentType: '',
    isPinned: false,
    orderIndex: 0,
    publish: false,
    addPastQuestions: false,
    topicHint: ''
  });

  // Initialize states with draft if available (only for create mode)
  const [formData, setFormData] = useState(() => {
    if (!lessonId) { // Only load draft in create mode
      const draftKey = `lesson-draft-${courseId}`;
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          console.log('🔄 Initializing with draft formData');
          return {
            ...getDefaultFormData(),
            ...draft.formData,
            addPastQuestions: !!draft.formData?.addPastQuestions,
            topicHint: draft.formData?.topicHint || ''
          };
        } catch (error) {
          console.error('Error parsing draft:', error);
        }
      }
    }
    return getDefaultFormData();
  });

  const [contentBlocks, setContentBlocks] = useState(() => {
    if (!lessonId) { // Only load draft in create mode
      const draftKey = `lesson-draft-${courseId}`;
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          console.log('🔄 Initializing with draft contentBlocks:', draft.contentBlocks);
          return draft.contentBlocks;
        } catch (error) {
          console.error('Error parsing draft:', error);
        }
      }
    }
    return [{ id: Date.now(), type: 'text', content: '', file: null, fileName: '' }];
  });

  const [loading, setLoading] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [allQuizzes, setAllQuizzes] = useState([]); // Store all course quizzes
  const [allLessons, setAllLessons] = useState([]); // Store all lessons for filtering
  const [selectedQuizzes, setSelectedQuizzes] = useState(() => {
    if (!lessonId) { // Only load draft in create mode
      const draftKey = `lesson-draft-${courseId}`;
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          console.log('🔄 Initializing with draft selectedQuizzes');
          return draft.selectedQuizzes || [];
        } catch (error) {
          console.error('Error parsing draft:', error);
        }
      }
    }
    return [];
  });
  const [originalLesson, setOriginalLesson] = useState(null); // Store original lesson data
  const [previewMode, setPreviewMode] = useState({}); // Track which text blocks are in preview mode
  const [dataLoaded, setDataLoaded] = useState(!isEdit); // Track if initial data (lesson or draft) is loaded
  const [uploadProgress, setUploadProgress] = useState(0); // Track upload progress
  const [uploadStatus, setUploadStatus] = useState(''); // Track upload status message
  const [isUploading, setIsUploading] = useState(false); // Track if currently uploading
  const [uploadStartTime, setUploadStartTime] = useState(null); // Track upload start time
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null); // Estimated time left
  const [cancelTokenSource, setCancelTokenSource] = useState(null); // Track cancel token for aborting upload
  const [uploadError, setUploadError] = useState(() => {
    // Check if there was a previous upload attempt
    const uploadState = localStorage.getItem('upload-state');
    if (uploadState) {
      try {
        const state = JSON.parse(uploadState);
        if (state.lessonKey === (lessonId || `new-${courseId}`) && state.inProgress) {
          return true; // Show retry UI on page load
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    return false;
  });
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts

  // Restore upload state on mount
  useEffect(() => {
    const uploadState = localStorage.getItem('upload-state');
    if (uploadState) {
      try {
        const state = JSON.parse(uploadState);
        if (state.lessonKey === (lessonId || `new-${courseId}`) && state.inProgress) {
          setUploadProgress(state.progress || 0);
          setUploadStatus(`Previous upload interrupted at ${state.progress}% - Click retry to continue`);
          setIsUploading(true);
        }
      } catch (e) {
        console.error('Error restoring upload state:', e);
      }
    }
  }, [lessonId, courseId]);

  // Prevent accidental page close during upload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isUploading && !uploadError) {
        e.preventDefault();
        e.returnValue = 'Upload in progress! Are you sure you want to leave? Your progress will be lost but your draft is saved.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUploading, uploadError]);

  // Save draft to localStorage whenever content changes
  useEffect(() => {
    if (dataLoaded && (formData.title || contentBlocks.some(block => block.content))) {
      const draftKey = lessonId ? `lesson-edit-draft-${lessonId}` : `lesson-draft-${courseId}`;
      const draft = {
        formData,
        contentBlocks,
        selectedQuizzes,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(draftKey, JSON.stringify(draft));
      console.log('💾 Draft saved to localStorage:', {
        mode: lessonId ? 'EDIT' : 'CREATE',
        blockCount: contentBlocks.length,
        blocks: contentBlocks.map(b => ({ type: b.type, hasContent: !!b.content }))
      });
    }
  }, [formData, contentBlocks, selectedQuizzes, courseId, lessonId, dataLoaded]);

  const fetchQuizzes = useCallback(async () => {
    try {
      console.log('Fetching quizzes for courseId:', courseId);
      
      // Fetch all quizzes for this course
      const quizzesRes = await api.get(`/quizzes?limit=100`);
      const quizData = quizzesRes.data.data || quizzesRes.data;
      const quizArray = Array.isArray(quizData) ? quizData : [];
      const filteredQuizzes = quizArray.filter(q => q.courseId == courseId);
      console.log('Filtered quizzes for this course:', filteredQuizzes.length);
      setAllQuizzes(filteredQuizzes);
      
      // Fetch all lessons for this course to get already-attached quizzes
      const lessonsRes = await api.get(`/lessons/course/${courseId}`);
      const lessons = lessonsRes.data || [];
      setAllLessons(lessons);
      
    } catch (err) {
      console.error('Error fetching quizzes:', err);
      setAllQuizzes([]);
      setAllLessons([]);
    }
  }, [courseId]);
  
  const filterAvailableQuizzes = useCallback(() => {
    // Collect all quiz IDs that are already attached to OTHER lessons
    const attachedQuizIds = new Set();
    allLessons.forEach(lesson => {
      if (lesson.quizzes && Array.isArray(lesson.quizzes)) {
        lesson.quizzes.forEach(quiz => {
          // Don't exclude quizzes attached to the current lesson being edited
          if (!isEdit || !originalLesson || lesson.id !== originalLesson.id) {
            attachedQuizIds.add(quiz.id);
          }
        });
      }
    });
    
    // Keep currently selected quizzes visible in the list (checked) —
    // only exclude quizzes that are attached to OTHER lessons.
    const availableQuizzes = allQuizzes.filter(q => !attachedQuizIds.has(q.id));
    
    console.log('Available quizzes after filtering (excluding attached to other lessons):', availableQuizzes.length);
    setQuizzes(availableQuizzes);
  }, [allQuizzes, allLessons, isEdit, originalLesson]);

  const fetchLesson = useCallback(async () => {
    try {
      const res = await api.get(`/lessons/${lessonId}`);
      const lesson = res.data;
      console.log('Fetched lesson for editing:', lesson);
      
      // Store the original lesson data
      setOriginalLesson(lesson);
      
      setFormData({
        title: lesson.title,
        description: lesson.description || '',
        contentType: lesson.contentType,
        textContent: lesson.textContent || '',
        contentUrl: lesson.contentUrl || '',
        studentType: lesson.studentType || '',
        isPinned: lesson.isPinned,
        orderIndex: lesson.orderIndex,
        publish: !!lesson.publishedAt,
        addPastQuestions: !!lesson.addPastQuestions,
        topicHint: lesson.topicHint || ''
      });
      
      // Load content blocks if they exist
      if (lesson.contentBlocks && Array.isArray(lesson.contentBlocks)) {
        console.log('Loading content blocks:', lesson.contentBlocks);
        setContentBlocks(lesson.contentBlocks.map((block, index) => ({
          id: Date.now() + index,
          type: block.type,
          content: block.content || '',
          contentUrl: block.contentUrl || '',
          file: null,
          fileName: ''
        })));
      }
      
      // Set attached quizzes
      if (lesson.quizzes) {
        setSelectedQuizzes(lesson.quizzes.map(q => ({
          id: q.id,
          isRequired: q.lessonQuizMeta?.isRequired || false
        })));
      }
      
      // After loading lesson data, check if there's a draft to restore
      const draftKey = `lesson-edit-draft-${lessonId}`;
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          console.log('🔄 Found edit draft, restoring...');
          setFormData({
            ...getDefaultFormData(),
            ...draft.formData,
            addPastQuestions: !!draft.formData?.addPastQuestions,
            topicHint: draft.formData?.topicHint || ''
          });
          setContentBlocks(draft.contentBlocks);
          setSelectedQuizzes(draft.selectedQuizzes || []);
        } catch (error) {
          console.error('Error restoring edit draft:', error);
        }
      }
      
      // Mark data as loaded so auto-save can start
      setDataLoaded(true);
    } catch (err) {
      console.error('Error fetching lesson:', err);
      alert('Failed to load lesson');
    }
  }, [lessonId]);

  // useEffects that depend on the callback functions
  useEffect(() => {
    if (isEdit) {
      fetchLesson();
    }
  }, [isEdit, fetchLesson]);
  
  // Fetch quizzes after originalLesson is loaded (for edit mode) or immediately (for create mode)
  useEffect(() => {
    if (!isEdit || originalLesson) {
      fetchQuizzes();
    }
  }, [isEdit, originalLesson, fetchQuizzes]);
  
  // Filter available quizzes whenever selectedQuizzes changes
  useEffect(() => {
    filterAvailableQuizzes();
  }, [filterAvailableQuizzes]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: nextValue,
      ...(name === 'addPastQuestions' && !nextValue ? { topicHint: '' } : {})
    }));
  };

  // Content block management functions
  const addContentBlock = () => {
    setContentBlocks(prev => [
      ...prev,
      { id: Date.now(), type: 'text', content: '', file: null, fileName: '' }
    ]);
  };

  const removeContentBlock = (blockId) => {
    if (contentBlocks.length === 1) {
      alert('At least one content block is required');
      return;
    }
    setContentBlocks(prev => prev.filter(block => block.id !== blockId));
  };

  const updateContentBlock = (blockId, field, value) => {
    setContentBlocks(prev => prev.map(block =>
      block.id === blockId ? { ...block, [field]: value } : block
    ));
  };

  const handleBlockFileChange = (blockId, e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      updateContentBlock(blockId, 'file', selectedFile);
      updateContentBlock(blockId, 'fileName', selectedFile.name);
    }
  };

  const togglePreviewMode = (blockId) => {
    setPreviewMode(prev => ({
      ...prev,
      [blockId]: !prev[blockId]
    }));
  };

  const handleQuizToggle = (quizId) => {
    setSelectedQuizzes(prev => {
      const exists = prev.find(q => q.id === quizId);
      if (exists) {
        return prev.filter(q => q.id !== quizId);
      } else {
        return [...prev, { id: quizId, isRequired: false }];
      }
    });
  };

  const handleQuizRequiredToggle = (quizId) => {
    setSelectedQuizzes(prev => 
      prev.map(q => q.id === quizId ? { ...q, isRequired: !q.isRequired } : q)
    );
  };

  const handleCancelUpload = () => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Upload cancelled by user');
      setCancelTokenSource(null);
      setIsUploading(false);
    }
  };

  // Direct upload to R2 for large files (bypasses 30s Heroku timeout)
  const uploadToR2Direct = async (file, blockType) => {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          console.log(`📦 Direct uploading ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB) to R2...`);
          
          // Step 1: Get presigned URL from backend
          setUploadStatus('Getting upload URL...');
          
          // Determine folder based on file type
          let folder = 'documents';
          if (blockType === 'video' || file.type.startsWith('video/')) {
            folder = 'videos';
          } else if (blockType === 'audio' || file.type.startsWith('audio/')) {
            folder = 'audios';
          } else if (file.type.startsWith('image/')) {
            folder = 'images';
          }
          
          const presignedResponse = await api.post('/upload/get-r2-presigned-url', {
            fileName: file.name,
            fileType: file.type,
            folder: folder
          });

          const { uploadUrl, publicUrl } = presignedResponse.data;
          console.log('✅ Got R2 presigned URL, uploading directly...');
          console.log('📁 Folder:', folder);

          // Step 2: Upload file directly to R2 using presigned URL
          const xhr = new XMLHttpRequest();

          // Track upload progress
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const percentCompleted = Math.round((e.loaded * 100) / e.total);
              setUploadProgress(percentCompleted);
              setUploadStatus(`Uploading to R2... ${percentCompleted}%`);

              // Calculate estimated time remaining
              if (uploadStartTime && percentCompleted > 5 && percentCompleted < 100) {
                const elapsed = Date.now() - uploadStartTime;
                const estimatedTotal = (elapsed / percentCompleted) * 100;
                const remainingMs = estimatedTotal - elapsed;
                const minutes = Math.floor(remainingMs / 60000);
                const seconds = Math.floor((remainingMs % 60000) / 1000);
                setEstimatedTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
              }
            }
          });

          // Handle completion
          xhr.addEventListener('load', () => {
            if (xhr.status === 200) {
              console.log(`✅ File uploaded to R2:`, publicUrl);
              resolve(publicUrl);
            } else {
              console.error('❌ R2 upload failed:', xhr.status, xhr.responseText);
              reject(new Error(`Upload failed: ${xhr.statusText}`));
            }
          });

          // Handle errors
          xhr.addEventListener('error', () => {
            console.error('❌ R2 upload error');
            reject(new Error('Upload failed: Network error'));
          });

          // Handle abort (cancel)
          xhr.addEventListener('abort', () => {
            console.log('⚠️ Upload cancelled by user');
            reject(new Error('Upload cancelled'));
          });

          // Open connection and send
          xhr.open('PUT', uploadUrl);
          xhr.setRequestHeader('Content-Type', file.type);
          xhr.send(file);

          // Store xhr for cancellation
          if (cancelTokenSource) {
            cancelTokenSource.cancel = () => xhr.abort();
          }

        } catch (error) {
          console.error('❌ Error in R2 direct upload:', error);
          reject(error);
        }
      })();
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Please enter a lesson title');
      return;
    }

    // Validate content blocks
    const hasContent = contentBlocks.some(block => {
      if (block.type === 'text' && block.content.trim()) return true;
      if (['image', 'video', 'audio', 'document'].includes(block.type) && (block.file || block.contentUrl)) return true;
      if (block.type === 'link' && block.content.trim()) return true;
      return false;
    });

    if (!hasContent) {
      alert('Please add at least one content block with content');
      return;
    }

    setLoading(true);
    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus('Preparing files...');
    setUploadError(false);
    setUploadStartTime(Date.now());
    setEstimatedTimeRemaining(null);

    // Create cancel token for this upload
    const cancelSource = CancelToken.source();
    setCancelTokenSource(cancelSource);

    // Save upload state
    const uploadStateKey = lessonId || `new-${courseId}`;
    localStorage.setItem('upload-state', JSON.stringify({
      lessonKey: uploadStateKey,
      inProgress: true,
      progress: 0,
      timestamp: new Date().toISOString()
    }));

    // Add timeout detection
    const uploadTimeout = setTimeout(() => {
      if (uploadProgress < 100 && isUploading) {
        setUploadStatus('Upload seems slow. Check your network connection...');
      }
    }, 30000); // Alert after 30 seconds if still uploading

    try {
      const data = new FormData();
      data.append('courseId', courseId);
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('studentType', formData.studentType);
      data.append('isPinned', formData.isPinned);
      data.append('orderIndex', formData.orderIndex);
      data.append('publish', formData.publish);
    data.append('addPastQuestions', formData.addPastQuestions);
    data.append('topicHint', formData.addPastQuestions ? formData.topicHint : '');

      // Process files: Check size and route to appropriate storage
      const processedBlocks = await Promise.all(
        contentBlocks.map(async (block, index) => {
          if (block.file) {
            const fileSizeMB = block.file.size / (1024 * 1024);
            console.log(`File ${block.file.name}: ${fileSizeMB.toFixed(2)}MB`);
            
            // If file is >30MB, upload directly to R2 (bypasses Heroku 30s timeout)
            // Files ≤30MB go through backend (should complete within timeout)
            if (fileSizeMB > 30) {
              console.log(`📦 File is large (${fileSizeMB.toFixed(2)}MB), uploading directly to R2...`);
              try {
                const r2Url = await uploadToR2Direct(block.file, block.type);
                return {
                  type: block.type,
                  content: block.content,
                  order: index,
                  contentUrl: r2Url // Use R2 URL
                };
              } catch (error) {
                console.error(`Failed to upload ${block.file.name} to R2:`, error);
                throw new Error(`Failed to upload ${block.file.name}: ${error.message}`);
              }
            } else {
              // File ≤30MB - upload through backend (Heroku → R2)
              console.log(`📤 File is ${fileSizeMB.toFixed(2)}MB, uploading via backend...`);
              return {
                type: block.type,
                content: block.content,
                order: index,
                contentUrl: block.contentUrl || null,
                _fileIndex: index // Mark for Heroku upload
              };
            }
          } else {
            return {
              type: block.type,
              content: block.content,
              order: index,
              contentUrl: block.contentUrl || null
            };
          }
        })
      );

      // Add processed content blocks as JSON
      data.append('contentBlocks', JSON.stringify(processedBlocks));

      // Append files that will go through backend (≤50MB)
      // Files >50MB are uploaded directly to R2 (Cloudflare) and don't need to be sent here
      contentBlocks.forEach((block, index) => {
        if (block.file) {
          const fileSizeMB = block.file.size / (1024 * 1024);
          if (fileSizeMB <= 50) {
            data.append(`files`, block.file);
            data.append(`fileIndices`, index); // Track which block each file belongs to
          }
        }
      });

      setUploadStatus('Saving lesson...');

      let lesson;
      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
        cancelToken: cancelSource.token,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          setUploadStatus(`Uploading... ${percentCompleted}%`);
          
          // Calculate estimated time remaining
          if (uploadStartTime && percentCompleted > 5 && percentCompleted < 100) {
            const elapsed = Date.now() - uploadStartTime;
            const estimatedTotal = (elapsed / percentCompleted) * 100;
            const remainingMs = estimatedTotal - elapsed;
            const minutes = Math.floor(remainingMs / 60000);
            const seconds = Math.floor((remainingMs % 60000) / 1000);
            setEstimatedTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
          }
          
          // Update localStorage with progress
          localStorage.setItem('upload-state', JSON.stringify({
            lessonKey: uploadStateKey,
            inProgress: true,
            progress: percentCompleted,
            timestamp: new Date().toISOString()
          }));
        }
      };

      if (isEdit) {
        const res = await api.put(`/lessons/${lessonId}`, data, config);
        lesson = res.data;
      } else {
        const res = await api.post('/lessons', data, config);
        lesson = res.data;
      }

      setUploadStatus('Processing quizzes...');

      // Pin/unpin quizzes
      if (lesson && lesson.id) {
        // Get current quizzes from original lesson data (not from update response)
        const currentQuizIds = isEdit && originalLesson && originalLesson.quizzes 
          ? originalLesson.quizzes.map(q => q.id) 
          : [];
        
        console.log('Current quiz IDs:', currentQuizIds);
        console.log('Selected quiz IDs:', selectedQuizzes.map(q => q.id));
        
        // Pin new quizzes
        for (const quiz of selectedQuizzes) {
          if (!currentQuizIds.includes(quiz.id)) {
            console.log('Pinning quiz:', quiz.id);
            await api.post(`/lessons/${lesson.id}/pin-quiz`, {
              quizId: quiz.id,
              isRequired: quiz.isRequired,
              orderIndex: 0
            });
          }
        }

        // Unpin removed quizzes
        for (const currentQuizId of currentQuizIds) {
          if (!selectedQuizzes.find(q => q.id === currentQuizId)) {
            console.log('Unpinning quiz:', currentQuizId);
            await api.delete(`/lessons/${lesson.id}/unpin-quiz/${currentQuizId}`);
          }
        }
      }

      clearTimeout(uploadTimeout);
      setUploadStatus('Success!');
      setRetryCount(0);
      setUploadStartTime(null);
      setEstimatedTimeRemaining(null);
      
      // Clear upload state
      localStorage.removeItem('upload-state');
      
      alert(isEdit ? 'Lesson updated successfully!' : 'Lesson created successfully!');
      
      // Clear draft from localStorage after successful submission
      const draftKey = lessonId ? `lesson-edit-draft-${lessonId}` : `lesson-draft-${courseId}`;
      localStorage.removeItem(draftKey);
      console.log('🗑️ Draft cleared from localStorage');
      
      navigateBack(navigate, location, null, getCourseLessonsPath(courseId, location, null)); // Go back to previous page
    } catch (err) {
      clearTimeout(uploadTimeout);
      console.error('Error saving lesson:', err);
      
      // Check if upload was cancelled
      if (isCancel(err)) {
        console.log('Upload cancelled by user');
        setUploadStatus('Upload cancelled');
        setUploadError(true);
        
        // Update upload state with cancellation
        localStorage.setItem('upload-state', JSON.stringify({
          lessonKey: uploadStateKey,
          inProgress: false,
          progress: uploadProgress,
          error: true,
          cancelled: true,
          timestamp: new Date().toISOString()
        }));
        
        return; // Don't show retry for cancelled uploads
      }
      
      setUploadError(true);
      setUploadStatus(`Upload failed at ${uploadProgress}%`);
      setRetryCount(prev => prev + 1);
      
      // Update upload state with error
      localStorage.setItem('upload-state', JSON.stringify({
        lessonKey: uploadStateKey,
        inProgress: true,
        progress: uploadProgress,
        error: true,
        timestamp: new Date().toISOString()
      }));
      
      const errorMessage = err.response?.data?.error || 'Failed to save lesson';
      
      // Check if it's a network error or timeout
      if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || !err.response) {
        setUploadStatus(`Network error at ${uploadProgress}% - Click retry to continue`);
      } else {
        setUploadStatus(`Error: ${errorMessage.substring(0, 50)}`);
      }
      
      // Don't show alert, let user use retry button instead
    } finally {
      setLoading(false);
      setCancelTokenSource(null); // Clear cancel token
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigateBack(navigate, location, null, getCourseLessonsPath(courseId, location, null))}
          className="text-blue-600 hover:text-blue-800"
        >
          ← Back to Courses
        </button>
        <div className="flex items-center justify-between mt-4">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? 'Edit Lesson' : 'Create New Lesson'}
          </h1>
          {(() => {
            const draftKey = lessonId ? `lesson-edit-draft-${lessonId}` : `lesson-draft-${courseId}`;
            return localStorage.getItem(draftKey) && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Are you sure you want to clear the saved draft?')) {
                    localStorage.removeItem(draftKey);
                    window.location.reload();
                  }
                }}
                className="text-sm text-red-600 hover:text-red-800 underline"
              >
                Clear Draft
              </button>
            );
          })()}
        </div>
        {(() => {
          const draftKey = lessonId ? `lesson-edit-draft-${lessonId}` : `lesson-draft-${courseId}`;
          return localStorage.getItem(draftKey) && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                💾 <strong>Draft restored:</strong> Your unsaved changes have been recovered.
              </p>
            </div>
          );
        })()}
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Title */}
        <div>
          <label htmlFor="lesson-title" className="block text-sm font-medium text-gray-700 mb-2">
            Lesson Title *
          </label>
          <input
            id="lesson-title"
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g.,Lesson 1: Introduction to Biology"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="lesson-description" className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            id="lesson-description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Optional lesson description..."
          />
        </div>

        {/* Content Blocks */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="block text-sm font-medium text-gray-700">
              Lesson Content * (Add multiple text, images, videos, etc.)
            </span>
            <button
              type="button"
              onClick={addContentBlock}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
            >
              <PlusIcon className="h-4 w-4" />
              Add Content Block
            </button>
          </div>

          {/* File Upload Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              ⚠️ <strong>Large File Upload Tips:</strong>
            </p>
            <ul className="text-xs text-yellow-700 mt-1 ml-4 list-disc space-y-1">
              <li>Maximum file size: 200MB per file</li>
              <li>Stable internet required - uploads may fail on slow/unstable connections</li>
              <li>Your progress is auto-saved as draft </li>
            </ul>
          </div>

          {contentBlocks.map((block, index) => (
            <div key={block.id} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-start mb-3">
                <span className="text-sm font-medium text-gray-700">Block {index + 1}</span>
                {contentBlocks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeContentBlock(block.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>

              {/* Block Type Selector */}
              <div className="mb-3">
                <select
                  value={block.type}
                  onChange={(e) => updateContentBlock(block.id, 'type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="text">Text</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="audio">Audio</option>
                  <option value="document">Document (PDF/Word)</option>
                  <option value="link">External Link</option>
                </select>
              </div>

              {/* Block Content */}
              {block.type === 'text' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      {previewMode[block.id] ? 'Preview' : 'Edit Text Content'}
                    </label>
                    <button
                      type="button"
                      onClick={() => togglePreviewMode(block.id)}
                      className="flex items-center gap-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      {previewMode[block.id] ? (
                        <>
                          <PencilIcon className="h-4 w-4" />
                          Edit
                        </>
                      ) : (
                        <>
                          <EyeIcon className="h-4 w-4" />
                          Preview
                        </>
                      )}
                    </button>
                  </div>
                  
                  {previewMode[block.id] ? (
                    <div className="w-full border border-gray-300 rounded-lg bg-gray-50 p-4 min-h-[120px]">
                      {block.content ? (
                        <LessonTextRenderer content={block.content} />
                      ) : (
                        <p className="text-gray-400 italic">No content to preview. Switch to edit mode to add text.</p>
                      )}
                    </div>
                  ) : (
                    <RichTextEditor
                      value={block.content}
                      onChange={(e) => updateContentBlock(block.id, 'content', e.target.value)}
                      placeholder="Enter text content... You can use the toolbar to format your text like in Microsoft Word."
                    />
                  )}
                </div>
              )}

              {block.type === 'link' && (
                <input
                  type="url"
                  value={block.content}
                  onChange={(e) => updateContentBlock(block.id, 'content', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com/resource"
                />
              )}

              {['image', 'video', 'audio', 'document'].includes(block.type) && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center bg-white">
                  <CloudArrowUpIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                  <input
                    type="file"
                    onChange={(e) => handleBlockFileChange(block.id, e)}
                    accept={
                      block.type === 'image' ? 'image/*' :
                      block.type === 'video' ? 'video/*' :
                      block.type === 'audio' ? 'audio/*,.mp3,.m4a,.wav,.aac,.ogg,.opus,.oga,.webm,.flac,.wma' :
                      '.pdf,.doc,.docx'
                    }
                    className="hidden"
                    id={`file-upload-${block.id}`}
                  />
                  <label htmlFor={`file-upload-${block.id}`} className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-800">Choose a file</span>
                  </label>
                  {block.fileName && (
                    <p className="mt-2 text-sm text-gray-600">Selected: {block.fileName}</p>
                  )}
                  {block.contentUrl && !block.fileName && (
                    <p className="mt-2 text-sm text-green-600">Current file uploaded</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {block.type === 'audio' ? '✅ All formats supported including WhatsApp audio (auto-converts to MP3 for iOS)' :
                     block.type === 'video' ? 'All video formats accepted (MP4, MOV, M4V, AVI, WebM, MKV, etc.)' :
                     block.type === 'image' ? 'Supported: JPG, PNG, GIF, WebP' :
                     'Supported: PDF, DOC, DOCX'}
                  </p>
                  <p className="text-xs text-gray-500">Max size: 200MB</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Student Type */}
        <div>
          <label htmlFor="student-type" className="block text-sm font-medium text-gray-700 mb-2">
            Target Class
          </label>
          <select
            id="student-type"
            name="studentType"
            value={formData.studentType}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Students</option>
            <option value="shs1">SHS 1 Only</option>
            <option value="shs2">SHS 2 Only</option>
            <option value="shs3">SHS 3 Only</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Leave as &quot;All Students&quot; to show to everyone
          </p>
        </div>

        {/* Order Index */}
        <div>
          <label htmlFor="order-index" className="block text-sm font-medium text-gray-700 mb-2">
            Display Order
          </label>
          <input
            id="order-index"
            type="number"
            name="orderIndex"
            value={formData.orderIndex}
            onChange={handleChange}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            Lower numbers appear first (0, 1, 2...)
          </p>
        </div>

        {/* Checkboxes */}
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              id="is-pinned"
              type="checkbox"
              name="isPinned"
              checked={formData.isPinned}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is-pinned" className="ml-2 text-sm text-gray-700">
              Pin this lesson (appears at the top)
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="publish-now"
              type="checkbox"
              name="publish"
              checked={formData.publish}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="publish-now" className="ml-2 text-sm text-gray-700">
              Publish immediately (visible to students)
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="add-past-questions"
              type="checkbox"
              name="addPastQuestions"
              checked={!!formData.addPastQuestions}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="add-past-questions" className="ml-2 text-sm text-gray-700">
              Add related past questions in lesson view (matches by course + lesson title)
            </label>
          </div>
        </div>

        <div>
          <label htmlFor="topic-hint" className="block text-sm font-medium text-gray-700 mb-2">
            Topic Hint for Bot (Optional)
          </label>
          <input
            id="topic-hint"
            type="text"
            name="topicHint"
            value={formData.topicHint || ''}
            onChange={handleChange}
            disabled={!formData.addPastQuestions}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
            placeholder="e.g., Trigonometry identities"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enable &quot;Add past questions&quot; to provide a hint used when selecting similar questions.
          </p>
        </div>

        {/* Attach Quizzes */}
        <div>
          <div className="block text-sm font-medium text-gray-700 mb-2">
            Attach Quizzes (Optional)
          </div>
          {quizzes.length > 0 ? (
            <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
              {quizzes.map(quiz => {
                const isSelected = selectedQuizzes.find(q => q.id === quiz.id);
                return (
                  <div key={quiz.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                    <div className="flex items-center flex-1">
                      <input
                        id={`quiz-${quiz.id}`}
                        type="checkbox"
                        checked={!!isSelected}
                        onChange={() => handleQuizToggle(quiz.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`quiz-${quiz.id}`} className="ml-2 text-sm text-gray-700">{quiz.title}</label>
                    </div>
                    {isSelected && (
                      <div className="flex items-center text-xs">
                        <input
                          id={`quiz-required-${quiz.id}`}
                          type="checkbox"
                          checked={!!isSelected.isRequired}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleQuizRequiredToggle(quiz.id);
                          }}
                          className="h-3 w-3 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <label 
                          htmlFor={`quiz-required-${quiz.id}`} 
                          className="ml-1 text-red-600 cursor-pointer select-none"
                        >
                          Required
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 text-center text-gray-500 text-sm">
              No quizzes available for this course. Create quizzes first to attach them to lessons.
            </div>
          )}
        </div>

        {/* Upload Progress */}
        {isUploading && (
          <div className={`border rounded-lg p-4 ${uploadError ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-sm font-semibold ${uploadError ? 'text-red-700' : 'text-blue-700'}`}>
                {uploadStatus}
              </span>
              <div className="flex items-center gap-3">
                {estimatedTimeRemaining && !uploadError && (
                  <span className="text-xs text-blue-600 font-medium">
                    ⏱️ ~{estimatedTimeRemaining} remaining
                  </span>
                )}
                <span className={`text-sm font-bold ${uploadError ? 'text-red-900' : 'text-blue-900'}`}>
                  {uploadProgress}%
                </span>
              </div>
            </div>
            <div className={`w-full rounded-full h-3 overflow-hidden ${uploadError ? 'bg-red-200' : 'bg-blue-200'}`}>
              <div 
                className={`h-3 rounded-full transition-all duration-300 ease-out ${uploadError ? 'bg-red-600' : 'bg-blue-600'}`}
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            {!uploadError ? (
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-blue-700">
                  💡 Uploading large files... Please don&apos;t close this page.
                </p>
                <button
                  type="button"
                  onClick={handleCancelUpload}
                  className="px-3 py-1.5 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 transition-colors font-medium"
                >
                  ✕ Cancel Upload
                </button>
              </div>
            ) : (
              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-red-700">
                  ⚠️ Upload interrupted. Your draft is saved. {retryCount > 0 && `(Retry attempt ${retryCount})`}
                </p>
                <button
                  type="button"
                  onClick={(e) => {
                    setUploadError(false);
                    setIsUploading(false);
                    setUploadProgress(0);
                    setUploadStatus('');
                    setEstimatedTimeRemaining(null);
                    handleSubmit(e);
                  }}
                  className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  🔄 Retry Upload
                </button>
              </div>
            )}
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? uploadStatus || 'Saving...' : isEdit ? 'Update Lesson' : 'Create Lesson'}
          </button>
          <button
            type="button"
            onClick={() => navigateBack(navigate, location, null, getCourseLessonsPath(courseId, location, null))}
            disabled={loading}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default LessonFormPage;
